import { useEffect, useState } from 'react';
import { AllieItem, AllieSkeleton } from '../../../components';
import { AllieCard } from '../../../components/Card';
import { AllieInfiniteScroll } from '../../../components/InfiniteScroll';
import { AllieList } from '../../../components/List';
import { AllieText } from '../../../components/Text';
import type { DatagridContainerProps } from './DataContainer.types';

const namespace = 'allie-data-container';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DatagridContainerMobileProps
  extends Omit<DatagridContainerProps & { shouldScrollToTop?: boolean }, 'columns'> {}

export const AllieDataContainerMobile: React.FC<DatagridContainerMobileProps> = ({
  loadServerRows,
  limit = 10,
  filters = {},
  sorting = [],
  renderItem,
  loadingText,
  noOlderRecordsText,
  onFetchError,
  abortSignal,
  showSkeleton = false,
  fetchStrategy = 'append',
  shouldScrollToTop = true,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState<number>(0);
  const [noOlderRecords, setNoOlderRecords] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const query = async (
    currentPage = 0,
    currentLimit: number,
    currentFilters: any,
    currentSorting: any
  ) => {
    setNoOlderRecords(false);
    const params: any = { page: currentPage, limit: currentLimit };
    if (Object.keys(currentFilters).length) params.filters = currentFilters;
    if (currentSorting.length) params.sort = currentSorting;

    return loadServerRows(params)
      .then((fetchedData) => {
        const newData =
          fetchStrategy === 'append'
            ? [...(page ? data : []), ...fetchedData.data]
            : fetchedData.data;
        setData(newData);
        setPage(page);
        setFirstLoad(false);

        return newData;
      })
      .catch(() => {
        !abortSignal?.aborted && onFetchError();
      });
  };

  useEffect(() => {
    const el = document.getElementsByClassName('page-content');
    shouldScrollToTop && el[0]?.scrollIntoView({ behavior: 'smooth' });
    query(0, limit, filters, sorting);
  }, [limit, filters, loadServerRows]);

  const onFetch = async (ev: any) => {
    const countBefore = data.length;
    const newData = await query(page + 1, limit, filters, sorting);
    ev.target.complete();
    if (countBefore === newData?.length) setNoOlderRecords(true);
  };

  if (firstLoad || showSkeleton) {
    const skeletonPlaceholder = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1];
    return (
      <AllieList style={{ margin: 0, backgroundColor: 'inherit' }}>
        {skeletonPlaceholder.map(() => (
          <AllieCard
            content={{
              style: { padding: 7.5 },
              children: (
                <AllieItem>
                  <AllieSkeleton
                    animated
                    style={{
                      width: '100%',
                      height: 30,
                    }}
                  />
                </AllieItem>
              ),
            }}
          />
        ))}
      </AllieList>
    );
  }

  return (
    <>
      <AllieList style={{ margin: 0, backgroundColor: 'inherit' }} className={namespace}>
        {data.map((item) => (
          <AllieCard
            content={{ style: { padding: 5 }, children: renderItem(item) }}
            className={`${namespace}__card`}
          />
        ))}
      </AllieList>
      {noOlderRecords && (
        <div style={{ paddingBottom: 12.5 }}>
          <AllieText type="body2">{noOlderRecordsText}</AllieText>
        </div>
      )}
      {!noOlderRecords && <AllieInfiniteScroll loadingText={loadingText} onFetch={onFetch} />}
    </>
  );
};
